// src/pages/ProPetsPage.js

import React, { useContext, useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import ProPetCard from '../components/ProPetCard';
import FilterHeaderPro from '../components/FilterHeaderPro';
import ModalSignIn from '../components/ModalSignIn';
import { AuthContext }  from '../context/AuthContext';
import { TabContext }   from '../context/TabContext';
import { ModalContext } from '../context/ModalContext';
import { useProPets }   from '../hooks/rq/useProPets';
import './LikesPage.css';

export default function ProPetsPage() {
  // ─── Contextos e ID de profesional ─────────────────────────────
  const { user, isAuthenticated } = useContext(AuthContext);
  const professionalId = user?.professional_id;
  const { activeTab } = useContext(TabContext);
  const { openModal } = useContext(ModalContext);

  // ─── Outlet context para filtros Pro ─────────────────────────────
  const {
    showFilterHeader,
    hideFilterHeader
  } = useOutletContext();

  // ─── Estados para filtros Pro (especie + nombre) ────────────────
  const [proFilters, setProFilters] = useState({
    species: null,
    name:    ''
  });

  // ─── 1️⃣ Hook React Query para pets de profesional ──────────────
  const {
    data,
    isLoading,
    isError,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    refetch
  } = useProPets(professionalId, proFilters);

  // ─── 2️⃣ Aplanar páginas y eliminar duplicados por ID ────────────
  const allPets = data?.pages.flatMap(page => page.results) ?? [];
  const petsMap = new Map();
  allPets.forEach(pet => petsMap.set(pet.id, pet));
  const pets = Array.from(petsMap.values());

  // ─── 3️⃣ Scroll infinito con React Query ────────────────────────
  useEffect(() => {
    const container = document.querySelector('.pro-container');
    if (!container) return;
    const onScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      if (
        scrollTop + clientHeight >= scrollHeight - 400 &&
        hasNextPage &&
        !isFetchingNextPage
      ) {
        fetchNextPage();
      }
    };
    container.addEventListener('scroll', onScroll);
    return () => container.removeEventListener('scroll', onScroll);
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  // ─── 4️⃣ Handlers de login/register (solo profesionales ven likes) ─
  const handleOpenLogin = () =>
    openModal(<ModalSignIn initialTab="login" />, { tab: activeTab });
  const handleOpenRegister = () =>
    openModal(<ModalSignIn initialTab="register" />, { tab: activeTab });

  return (
    <div className="likes-page-outer">
      {/* Mostrar FilterHeaderPro cuando lo soliciten */}
      {showFilterHeader && (
        <FilterHeaderPro
          initialFilters={proFilters}
          onApply={filters => {
            // Aplicar nuevos filtros y refetch
            setProFilters(filters);
            refetch();
          }}
          onHide={hideFilterHeader}
        />
      )}

      <div className="likes-page-inner pro-container">
        {/* Estados de no auth / permiso */}
        {!isAuthenticated ? (
          <div className="not-authenticated">
            <p>Para ver tus mascotas publicadas debes iniciar sesión.</p>
            <button className="btn" onClick={handleOpenLogin}>Iniciar sesión</button>
            <span className="separator">o</span>
            <button className="btn" onClick={handleOpenRegister}>Crear cuenta</button>
          </div>
        ) : !professionalId ? (
          <p>No tienes permisos para ver esta sección.</p>
        ) : (
          <>
            {/* Error al cargar datos */}
            {isError && (
              <div style={{ padding: '20px', textAlign: 'center' }}>
                <p>Error al cargar mascotas.</p>
                <button onClick={() => refetch()}>Reintentar</button>
              </div>
            )}

            {/* Listado de tarjetas de mascota */}
            {pets.map(pet => (
              <ProPetCard key={pet.id} pet={pet} />
            ))}

            {/* Indicador de carga inicial o siguiente página */}
            {(isLoading || isFetchingNextPage) && (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <p>Cargando mascotas…</p>
              </div>
            )}

            {/* Mensaje de final de listado */}
            {!hasNextPage && !isLoading && !isFetchingNextPage && (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <p>No hay más mascotas para mostrar.</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
